"use client";
import { ANALYTICS_CONSENT_VERSION, type AnalyticsEventName, type AnalyticsProperties } from "./analytics-contract";
import { createAnalyticsTracker, type AnalyticsConsent } from "./analytics-tracker";
import { siteTagsIdentifiers } from "./tags-client";
let tracker: ReturnType<typeof createAnalyticsTracker> | undefined;
let enabled = false;
let runtime: "loading" | "enabled" | "disabled" = "loading";
/** Opt-in until the server says otherwise: nothing defaults to granted before we know the region. */
let optIn = true;
let loading: Promise<void> | undefined;
const listeners = new Set<() => void>();
const anonymousKey = "codifly.analytics.anonymous.v1";
export function analyticsPrivacySignal() {
  return typeof navigator !== "undefined" && (navigator.doNotTrack === "1" || (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true);
}
/** Two consent categories, one cookie each, same version: `codifly_analytics` (first-party measurement and the
 * analytics tags) and `codifly_marketing` (Meta, LinkedIn, TikTok pixels and their server-side forwarding).
 *
 * Opt-out model (decision of 2026-09-20, the audience is Latin America): both categories are ON by default.
 * The first visit writes the default cookies so the server sees the same choice as the browser, and the
 * visitor can withdraw at any time from «Privacidad». A browser privacy signal (GPC/DNT) flips the default
 * to denied, but an explicit choice made here wins over the signal — the cookie is the visitor's own word. */
export const CONSENT_COOKIES = { analytics: "codifly_analytics", marketing: "codifly_marketing", sessionReplay: "codifly_session_replay" } as const;
export type ConsentCategory = keyof typeof CONSENT_COOKIES;
export const DEFAULT_CONSENT: Record<ConsentCategory, "granted" | "denied"> = { analytics: "granted", marketing: "granted", sessionReplay: "denied" };
function explicitConsentCookie(name: string): "granted" | "denied" | null {
  const values = document.cookie.split(";").map(v => v.trim()).filter(v => v.startsWith(`${name}=`));
  if (values.length !== 1) return null;
  if (values[0] === `${name}=${ANALYTICS_CONSENT_VERSION}.granted`) return "granted";
  return values[0] === `${name}=${ANALYTICS_CONSENT_VERSION}.denied` ? "denied" : null;
}
function readConsentCookie(category: ConsentCategory): AnalyticsConsent {
  if (typeof document === "undefined") return "unknown";
  return explicitConsentCookie(CONSENT_COOKIES[category]) ?? (analyticsPrivacySignal() || optIn ? "denied" : DEFAULT_CONSENT[category]);
}
/** True once the visitor has decided the analytics and marketing categories. */
export function hasConsentCookies() {
  return typeof document !== "undefined" && (["analytics", "marketing"] as const).every(category => explicitConsentCookie(CONSENT_COOKIES[category]) !== null);
}
export function readAnalyticsConsent(): AnalyticsConsent { return readConsentCookie("analytics"); }
export function readMarketingConsent(): AnalyticsConsent { return readConsentCookie("marketing"); }
/** Session replay is always opt-in, independent from aggregate analytics or marketing. */
export function readSessionReplayConsent(): AnalyticsConsent { return readConsentCookie("sessionReplay"); }
export function hasSessionReplayConsentCookie(): boolean { return typeof document !== "undefined" && explicitConsentCookie(CONSENT_COOKIES.sessionReplay) !== null; }
export function writeSessionReplayConsent(value: "granted" | "denied") {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIES.sessionReplay}=${ANALYTICS_CONSENT_VERSION}.${value}; Path=/; Max-Age=7776000; SameSite=Lax${secure}`;
  window.dispatchEvent(new Event("codifly:analytics-consent"));
}
export function writeConsent(choices: Partial<Record<ConsentCategory, "granted" | "denied">>) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  for (const category of Object.keys(CONSENT_COOKIES) as ConsentCategory[]) {
    const choice = choices[category];
    if (!choice) continue;
    document.cookie = `${CONSENT_COOKIES[category]}=${ANALYTICS_CONSENT_VERSION}.${choice}; Path=/; Max-Age=7776000; SameSite=Lax${secure}`;
    if (category === "analytics" && choice !== "granted") tracker?.reset();
  }
  if (choices.analytics) try { localStorage.setItem("codifly.analytics.preference", `${choices.analytics}:${Date.now()}`); } catch { /* Cookies remain authoritative. */ }
  window.dispatchEvent(new Event("codifly:analytics-consent"));
}
/** First visit without a privacy signal: persist the default (everything on) so /api/events, which only
 * trusts the cookie, agrees with what the browser already does. Returns true when it wrote something. */
export function ensureDefaultConsent() {
  // Opt-in regions (EEA/UK/CH, or unknown): nothing is written; the visitor is asked instead.
  if (typeof document === "undefined" || analyticsPrivacySignal() || optIn || runtime !== "enabled") return false;
  const choices = Object.fromEntries((["analytics", "marketing"] as const).map(category => [category,
    explicitConsentCookie(CONSENT_COOKIES[category]) ?? DEFAULT_CONSENT[category]])) as Pick<Record<ConsentCategory, "granted" | "denied">, "analytics" | "marketing">;
  if (hasConsentCookies()) return false;
  writeConsent(choices);
  return true;
}
/** Kept for callers that only know about analytics; marketing stays as it was (or denied if unset). */
export function writeAnalyticsConsent(value: "granted" | "denied") {
  writeConsent({ analytics: value, marketing: readMarketingConsent() === "granted" ? "granted" : "denied" });
}
export function readAnalyticsRuntime() { return runtime; }
/** True when the visitor must opt in (EEA/UK/CH or unknown region); read after the runtime loads. */
export function readOptInRequired() { return optIn; }
export function subscribeAnalyticsRuntime(notify: () => void) { listeners.add(notify); return () => { listeners.delete(notify); }; }
/** This credential-free first-party config request is not a collected event.
 * It lets static public pages use the runtime flag without disabling their cache. */
export function loadAnalyticsRuntime() {
  loading ??= (async () => {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch("/api/analytics/config", { credentials: "omit", cache: "no-store", redirect: "error", signal: controller.signal });
      const value = await response.json();
      enabled = response.ok && value.enabled === true && value.consentVersion === ANALYTICS_CONSENT_VERSION;
      optIn = value.optIn !== false;
    } catch { enabled = false; }
    finally {
      clearTimeout(timer); runtime = enabled ? "enabled" : "disabled";
      if (!enabled) tracker?.reset();
      for (const notify of listeners) notify();
    }
  })();
  return loading;
}
export function resetAnalytics() { tracker?.reset(); }
export function trackAnalytics(event: AnalyticsEventName, properties: AnalyticsProperties) {
  if (!enabled || typeof window === "undefined") return Promise.resolve(false);
  // The first event can fire before the consent UI mounts: persist the opt-out default now, or /api/events
  // (which trusts only the cookie) answers 403 to a visitor the browser already treats as granted.
  ensureDefaultConsent();
  tracker ??= createAnalyticsTracker({ consent: readAnalyticsConsent, transport: (url, options) => fetch(url, options), uuid: () => crypto.randomUUID(), identifiers: siteTagsIdentifiers,
    onFailure: stage => { if (process.env.NODE_ENV === "development") console.info(`[Codifly analytics] suppressed at ${stage}`); }, anonymous: {
    read: () => sessionStorage.getItem(anonymousKey), write: value => sessionStorage.setItem(anonymousKey, value), clear: () => {
      try { sessionStorage.removeItem(anonymousKey); } catch { /* A disabled store must not break opt-out. */ }
    },
  } });
  return tracker.track(event, properties);
}
